import find from 'lodash/find';
import { updatePipelineInfo } from 'js/actions/pipelines';
import { setError } from 'js/actions/request';
import * as actionTypes from 'js/constants/actionTypes';
import { PUT } from 'js/constants/requestMethods';
import { getEffectiveRole } from 'js/lib/ACLUtils';
import { canUpdateResource } from 'js/lib/flowValidationUtils';
import { CALL_PRIVATE_API } from 'js/middleware/api';

export interface TransferFlowOwnershipArgs {
  flowId: number;
  newOwnerId: number;
  newOrgId: number;
  includeDataCredentials: boolean;
  includeReusableCode: boolean;
  /** Key of the ACL list the Access tab shows (`${aclResourceType}${resourceId}`). */
  aclResourceType: string;
  resourceId: number;
}

export const NEW_OWNER_ADMIN_NOT_CONFIRMED =
  'Could not confirm Administrator access for the new owner, so ownership was not transferred.';

/**
 * Transfers a flow to another user. The flow is checked first, so nothing is
 * sent for a flow whose owner can't change. The new owner is then granted admin
 * access, and the owner change is sent only once that grant has succeeded and
 * the returned accessor list shows them as admin. Any failure rejects without
 * touching ownership.
 */
export const transferFlowOwnership =
  ({
    flowId,
    newOwnerId,
    newOrgId,
    includeDataCredentials,
    includeReusableCode,
    aclResourceType,
    resourceId,
  }: TransferFlowOwnershipArgs) =>
  async (dispatch: any, getState: () => any) => {
    // The check updatePipelineInfo makes before the owner change, run before the
    // grant too: a missing, archived or restoring flow would otherwise keep the
    // admin grant while ownership stays put.
    const validation = canUpdateResource(getState().entities.flowNodes?.[flowId]);

    if (!validation.canActivate) {
      const error = { message: validation.reason, code: 'UPDATE_BLOCKED' };
      dispatch(setError(error));
      throw error;
    }

    // PUT adds to the existing accessors (POST would replace the whole list).
    // It responds with the full list, which also refreshes the Access tab.
    const grant = await dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_ACL_REQUEST,
          actionTypes.UPDATE_ACL_SUCCESS,
          actionTypes.UPDATE_ACL_FAILURE,
        ],
        endpoint: `/flows/${flowId}/accessors`,
        payload: {
          accessors: [{ type: 'USER', id: newOwnerId, access_role: 'admin' }],
        },
        schema: null,
        requestMethod: PUT,
        requestInfo: {
          aclResourceType,
          id: resourceId,
        },
      },
    });

    const newOwnerAccessor = find(
      grant?.result,
      (accessor) => accessor.type === 'USER' && accessor.id === newOwnerId,
    );

    if (getEffectiveRole(newOwnerAccessor) !== 'admin') {
      const error = { message: NEW_OWNER_ADMIN_NOT_CONFIRMED };
      dispatch(setError(error));
      throw error;
    }

    return dispatch(
      updatePipelineInfo(flowId, {
        owner_id: newOwnerId,
        org_id: newOrgId,
        include_data_credentials: includeDataCredentials,
        include_code_containers: includeReusableCode,
      }),
    );
  };
